import React from 'react'
import { useNavigate } from 'react-router'
import { CreditApplication } from '_gql'

import { useMakeTestId } from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import { StatusButtons } from '@sputnik/components'
import ImageWrapper from '@sputnik/components/ImageWrapper/ImageWrapper'
import useDivvyUUID from '@sputnik/hooks/useDivvyUUID'
import StatusTile from '@sputnik/pages/Dashboard/StatusTile/StatusTile'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import { useReprocessCreditApplication } from './ReprocessCreditApplication.gql'
import { useUnfreezeAndResetCreditPullRetries } from './UnfreezeAndResetCreditPullRetries.gql'

interface FrozenCreditStatusTileProps {
  creditApp: CreditApplication
}

const FrozenCreditStatusTile = ({ creditApp }: FrozenCreditStatusTileProps) => {
  const makeTestId = useMakeTestId('FrozenCreditStatusTile')
  const showSuccessToast = useToast(TOAST_TYPE_SUCCESS) as any
  const showDangerToast = useToast(TOAST_TYPE_DANGER) as any
  const navigate = useNavigate()
  const { authorizedSigner, salesforceCreditId, lastUpdatedByApplicantAt } = creditApp
  const [companyDivvyUuid] = useDivvyUUID(salesforceCreditId)

  const [reprocessCreditApplication, { loading: reprocessLoading }] = useReprocessCreditApplication({
    variables: {
      reason: 'frozen',
      salesforceCreditId,
    },
    onCompleted: () => {
      showSuccessToast(
        <FormattedMessage
          defaultMessage='Application has been successfully been resubmitted.'
          id='sputnik.FrozenCreditStatusTile__HCbmUR'
        />,
        {
          autoHideDelay: 5000,
        },
      )
      logInfo('reprocessCreditApplication', {
        attributes: {
          creditAppId: salesforceCreditId,
          result: `Success. Sending user to ${PATHNAME_CONSTS.SUBMITTING_APPLICATION} page`,
        },
        eventName: 'Resubmit Application click',
      })

      navigate(`/app/${salesforceCreditId}/${PATHNAME_CONSTS.SUBMITTING_APPLICATION}`)
    },
    onError: (e) => {
      logError(e, {
        attributes: {
          action: 'reprocessCreditApplication',
          creditAppId: salesforceCreditId,
          result: 'Error when resubmitting credit application',
        },
        eventName: 'Resubmit Application click',
      })
      showDangerToast(
        <FormattedMessage
          defaultMessage='There was an error re-running your application, please request review.'
          id='sputnik.FrozenCreditStatusTile__Vwqqe8'
        />,
      )
    },
    refetchQueries: [' GetApplicationStatuses', 'GetFrozenStatus'],
  })

  const [unfreezeAndResetCreditPullRetries, { loading: unfreezeLoading }] = useUnfreezeAndResetCreditPullRetries({
    variables: {
      companyDivvyUuid: companyDivvyUuid,
      salesforceCreditId,
    },
    onCompleted: () => {
      showSuccessToast(
        <FormattedMessage
          defaultMessage='Application has been successfully sent to review.'
          id='sputnik.FrozenCreditStatusTile__CNH6Ya'
        />,
        {
          autoHideDelay: 5000,
        },
      )
      logInfo('unfreezeCreditApplicationRequest', {
        attributes: {
          creditAppId: salesforceCreditId,
          result: 'Success',
        },
        eventName: 'Request Review click',
      })
    },
    onError: (e) => {
      logError(e, {
        attributes: {
          action: 'unfreezeCreditApplicationRequest',
          creditAppId: salesforceCreditId,
          result: 'Error when requesting to unfreeze credit application',
        },
        eventName: 'Request Review click',
      })
      showDangerToast(
        <FormattedMessage
          defaultMessage='There was an error when requesting to unfreeze your application'
          id='sputnik.FrozenCreditStatusTile__51LlE6'
        />,
      )
    },
    refetchQueries: [' GetApplicationStatuses', 'GetFrozenStatus'],
  })

  const onResubmitApplicationClick = () => {
    reprocessCreditApplication()
  }

  const onRequestReviewClick = () => {
    unfreezeAndResetCreditPullRetries()
  }

  return (
    <div data-testid={makeTestId('')}>
      <StatusTile
        isAnErrorTile
        creditApp={creditApp}
        image={
          <ImageWrapper
            alt='card frozen inside an ice cube'
            imageName='dashboard-application-frozen-status'
          />
        }
        primaryInfo={
          <FormattedMessage
            defaultMessage="{firstName}’s credit is frozen. Application is saved but cannot be reviewed until {firstName}'s credit is unfrozen. Please contact Experian to unfreeze your credit and then resubmit the application from this dashboard. "
            id='sputnik.FrozenCreditStatusTile__cF3fPn'
            values={{ firstName: authorizedSigner?.firstName }}
          />
        }
        secondaryInfo={
          <FormattedMessage
            defaultMessage='Last updated on {date, date, long}'
            id='sputnik.FrozenCreditStatusTile__nqBLM8'
            values={{ date: new Date(lastUpdatedByApplicantAt) }}
          />
        }
        title={
          <FormattedMessage
            defaultMessage='Application under review'
            id='sputnik.FrozenCreditStatusTile__05muiR'
          />
        }
      >
        <StatusButtons
          primaryLoading={reprocessLoading}
          primaryText={
            <FormattedMessage
              defaultMessage='Resubmit application'
              id='sputnik.FrozenCreditStatusTile__Y7YXP/'
            />
          }
          secondaryLoading={unfreezeLoading}
          secondaryText={
            <FormattedMessage
              defaultMessage='Request review'
              id='sputnik.FrozenCreditStatusTile__K4Y60a'
            />
          }
          onPrimaryClick={onResubmitApplicationClick}
          onSecondaryClick={onRequestReviewClick}
        />
      </StatusTile>
    </div>
  )
}

export default FrozenCreditStatusTile
